import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import SpinnerButton from "@/components/SpinnerButton";
import TurnstileWidget from "@/components/TurnstileWidget";
import { publicApi } from "@/lib/api";
import { buildAttribution } from "@/lib/attribution";
import { loadDraft, saveDraft, clearDraft } from "@/lib/persistence";
import { trackLeadStarted, trackLeadSubmitted } from "@/lib/tracking";
import { usePageMeta } from "@/lib/usePageMeta";

const PUBLIC_HOME_JSONLD = {
  "@context": "https://schema.org",
  "@type": "JewelryStore",
  "name": "Swiss Connection Watches",
  "url": "https://swisswatch.com.my",
  "telephone": "+603-4810 9353",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Level 1, Menara Hap Seng 1, Jalan P. Ramlee",
    "addressLocality": "Kuala Lumpur",
    "postalCode": "50250",
    "addressCountry": "MY",
  },
  "openingHoursSpecification": [{
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    "opens": "09:00",
    "closes": "21:00",
  }],
};

const PURPOSES = ["Buy", "Sell", "Service"];

// Contextual hint shown under the Purpose dropdown to prime the customer
// on what to mention in the booking. Keeps the form lean but signals what
// we'd ideally like to see in their notes / message.
const PURPOSE_HINTS = {
  Buy: "If you\u2019re viewing specific references (e.g., Datejust sizes / Rolex Professional models), add notes in the booking so we can prepare.",
  Sell: "If you\u2019re selling: the full set where available (box, papers, spare links).",
};

// Where to return the customer after a completed flow. Priority:
//  1. The referrer if it came from swisswatch.com.my (e.g. they clicked
//     "Contact us" from a product page) — sends them back to the same page.
//  2. The cached `return_url` set on first visit (handles in-app nav loss).
//  3. The main marketing site as a sensible default.
const PARENT_SITE = "https://www.swisswatch.com.my/";
function rememberReturnUrl() {
  try {
    const ref = document.referrer || "";
    if (/(^|\.)swisswatch\.com\.my/i.test(new URL(ref).hostname || "")) {
      sessionStorage.setItem("scw_return_url", ref);
    }
  } catch { /* invalid URL is fine, just skip */ }
}
function getReturnUrl() {
  try {
    const stored = sessionStorage.getItem("scw_return_url");
    if (stored) return stored;
  } catch { /* ignore */ }
  return PARENT_SITE;
}

function useUtm() {
  const [params] = useSearchParams();
  // gclid / fbclid / fbp are silent paid-traffic identifiers. Captured
  // at landing time and persisted into All_Leads so the server-side
  // Meta CAPI build (Pass 25) can fire `Lead`/`Purchase` events back to
  // Meta when the lead progresses through Contacted → In Progress →
  // Closed Won. URL params win over scw_attr cookie which wins over
  // standalone _fbp — see lib/attribution.js for the full contract.
  const attr = buildAttribution(params);
  return {
    product: params.get("product") || "",
    utm_source: attr.utm_source,
    utm_medium: attr.utm_medium,
    utm_campaign: attr.utm_campaign,
    gclid: attr.gclid,
    fbclid: attr.fbclid,
    fbp: attr.fbp,
  };
}

// Pass 53 — a saved draft (autosaved on every keystroke, see
// saveDraft() below) is meant to recover a customer's IN-PROGRESS typing
// across an accidental refresh, not to override a fresh link they just
// clicked. Without this, a customer who abandons an enquiry on one
// watch and later clicks "Enquire Now" on a DIFFERENT watch gets served
// the first watch's stale product — silently wrong once Product became
// a required field. Mirrors attribution.js's own precedent: a fresh URL
// signal outranks anything already stored. Everything else in the
// draft (name/phone/email/message) is left untouched — only Product
// reflects the link actually clicked just now.
function draftInit(draftKey, defaults, urlProduct) {
  const draft = loadDraft(draftKey);
  if (!draft) return defaults;
  return urlProduct ? { ...draft, product: urlProduct } : draft;
}

function FieldRow({ children }) {
  return <div style={{ marginBottom: 22 }}>{children}</div>;
}

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} className="scw-label">{children}</label>;
}

// ---------- Email enquiry form ----------

function EmailForm({ cfg, utm, store, onBack, onLeaveSite }) {
  const navigate = useNavigate();
  const draftKey = "home-email";
  const init = draftInit(draftKey, { name: "", phone: "", email: "", product: utm.product, message: "" }, utm.product);
  const [form, setForm] = useState(init);
  const turnstileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const startedRef = useRef(false);
  const markStarted = () => {
    if (!startedRef.current) { startedRef.current = true; trackLeadStarted("email"); }
  };

  useEffect(() => { saveDraft(draftKey, form); }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const tok = (await turnstileRef.current?.getToken()) || "";
      const res = await publicApi.email({
        name: form.name, phone: form.phone, email: form.email,
        product: form.product, message: form.message,
        store,
        ...utm, website: "", turnstile_token: tok,
      });
      if (res.data.ok) {
        setDone(res.data.message);
        trackLeadSubmitted("email", form.product);
        clearDraft(draftKey);
      } else {
        setDone(res.data.message);
      }
    } catch (e2) {
      // Reset Turnstile so the next attempt uses a fresh token.
      try { turnstileRef.current?.reset(); } catch { /* ignore */ }
      setError(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="scw-anim-fade" style={{ textAlign: "center", padding: "40px 0" }} data-testid="email-thank-you">
        <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Thank you</p>
        <h2 style={{ fontSize: 28, lineHeight: 1.3, margin: 0 }}>{done}</h2>
        <button className="scw-btn-ghost scw-btn" style={{ marginTop: 32 }} onClick={onLeaveSite || onBack} data-testid="email-back-btn">Back to swisswatch.com.my</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} onFocus={markStarted} className="scw-anim-fade" data-testid="email-form">
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Send a message</p>
      <h2 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Tell us how we can help.</h2>
      <FieldRow>
        <Label htmlFor="ef-name">Name</Label>
        <input id="ef-name" className="scw-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="email-name-input" />
      </FieldRow>
      <FieldRow>
        <Label htmlFor="ef-phone">Phone</Label>
        <input id="ef-phone" className="scw-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Include country code" data-testid="email-phone-input" />
      </FieldRow>
      <FieldRow>
        <Label htmlFor="ef-email">Email</Label>
        <input id="ef-email" className="scw-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="email-email-input" />
      </FieldRow>
      <FieldRow>
        <Label htmlFor="ef-product">Watch or product of interest</Label>
        <input id="ef-product" className="scw-input" required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} data-testid="email-product-input" />
      </FieldRow>
      <FieldRow>
        <Label htmlFor="ef-message">Message</Label>
        <textarea id="ef-message" className="scw-textarea" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="email-message-input" />
      </FieldRow>
      {/* Honeypot */}
      <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{ display: "none" }} aria-hidden="true" />
      <TurnstileWidget siteKey={cfg.turnstile_site_key} ref={turnstileRef} />
      {error && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }} data-testid="email-error">{error}</p>}
      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <SpinnerButton variant="ghost" type="button" onClick={onBack} dataTestId="email-back-btn">Back</SpinnerButton>
        <SpinnerButton loading={loading} dataTestId="email-submit-btn">Send enquiry</SpinnerButton>
      </div>
    </form>
  );
}

// ---------- WhatsApp form ----------

function WhatsAppForm({ cfg, utm, store, onBack }) {
  const draftKey = "home-whatsapp";
  const init = draftInit(draftKey, { name: "", phone: "", product: utm.product }, utm.product);
  const [form, setForm] = useState(init);
  const turnstileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const startedRef = useRef(false);
  const markStarted = () => {
    if (!startedRef.current) { startedRef.current = true; trackLeadStarted("whatsapp"); }
  };

  useEffect(() => { saveDraft(draftKey, form); }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const tok = (await turnstileRef.current?.getToken()) || "";
      const res = await publicApi.whatsapp({
        name: form.name, phone: form.phone, product: form.product,
        store,
        ...utm, website: "", turnstile_token: tok,
      });
      if (res.data.ok && res.data.whatsapp_url) {
        trackLeadSubmitted("whatsapp", form.product);
        clearDraft(draftKey);
        window.location.href = res.data.whatsapp_url;
      } else {
        setError(res.data.message || "Something went wrong.");
      }
    } catch (e2) {
      try { turnstileRef.current?.reset(); } catch { /* ignore */ }
      setError(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} onFocus={markStarted} className="scw-anim-fade" data-testid="whatsapp-form">
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Chat on WhatsApp</p>
      <h2 style={{ fontSize: 28, margin: "0 0 28px 0" }}>We'll open WhatsApp with your details prefilled.</h2>
      <FieldRow>
        <Label htmlFor="wf-name">Name</Label>
        <input id="wf-name" className="scw-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="whatsapp-name-input" />
      </FieldRow>
      <FieldRow>
        <Label htmlFor="wf-phone">Phone</Label>
        <input id="wf-phone" className="scw-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Include country code" data-testid="whatsapp-phone-input" />
      </FieldRow>
      <FieldRow>
        <Label htmlFor="wf-product">Watch or product (optional)</Label>
        <input id="wf-product" className="scw-input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} data-testid="whatsapp-product-input" />
      </FieldRow>
      <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{ display: "none" }} aria-hidden="true" />
      <TurnstileWidget siteKey={cfg.turnstile_site_key} ref={turnstileRef} />
      {error && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }} data-testid="whatsapp-error">{error}</p>}
      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <SpinnerButton variant="ghost" type="button" onClick={onBack} dataTestId="whatsapp-back-btn">Back</SpinnerButton>
        <SpinnerButton loading={loading} dataTestId="whatsapp-submit-btn">Open WhatsApp</SpinnerButton>
      </div>
    </form>
  );
}

// ---------- Appointment booking ----------

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function dayLabel(idx) {
  return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][idx];
}
function calendarGrid(year, month) {
  // month is 0-indexed
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7; // start week Monday
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function AppointmentForm({ cfg, utm, store, onBack, onLeaveSite, onChooseWhatsApp }) {
  const navigate = useNavigate();
  const draftKey = "home-appointment";
  const init = draftInit(draftKey, {
    name: "", phone: "", email: "", purpose: "Buy", product: utm.product,
    appointment_date: "", appointment_time: "",
  }, utm.product);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(init);
  const turnstileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [calendar, setCalendar] = useState({ days: [] });
  const [viewMonth, setViewMonth] = useState(() => {
    const t = new Date();
    return { year: t.getFullYear(), month: t.getMonth() };
  });
  const [slots, setSlots] = useState([]);
  const startedRef = useRef(false);
  const markStarted = () => {
    if (!startedRef.current) { startedRef.current = true; trackLeadStarted("appointment"); }
  };

  useEffect(() => { saveDraft(draftKey, form); }, [form]);

  useEffect(() => {
    publicApi.calendar(monthKey(new Date(viewMonth.year, viewMonth.month, 1)), store)
      .then((r) => setCalendar(r.data))
      .catch(() => setCalendar({ days: [] }));
  }, [viewMonth, store]);

  useEffect(() => {
    if (!form.appointment_date) { setSlots([]); return; }
    publicApi.timeslots(form.appointment_date, store)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [form.appointment_date, store]);

  // Pass 43 — when the store toggle flips on the home screen and the
  // customer re-enters the appointment flow, any previously selected
  // date may now fall on the new store's closed day (e.g. they picked
  // Sunday on KL, then switched to JB which is open Sundays — keep
  // it; or they picked Monday on KL, then switched to JB — Monday is
  // closed, clear it). Conservative: clear date+time whenever store
  // changes so the customer never sees a stale "selected" cell.
  useEffect(() => {
    setForm((f) => ({ ...f, appointment_date: "", appointment_time: "" }));
  }, [store]);

  const dateMap = useMemo(() => {
    const m = {};
    for (const d of calendar.days || []) m[d.date] = d;
    return m;
  }, [calendar]);

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      const tok = (await turnstileRef.current?.getToken()) || "";
      const res = await publicApi.appointment({
        name: form.name, phone: form.phone, email: form.email,
        purpose: form.purpose, product: form.product,
        appointment_date: form.appointment_date, appointment_time: form.appointment_time,
        store,
        ...utm, website: "", turnstile_token: tok,
      });
      if (res.data.ok) {
        setDone(res.data.message);
        trackLeadSubmitted("appointment", form.product);
        clearDraft(draftKey);
      } else {
        setError(res.data.message || "Something went wrong.");
      }
    } catch (e) {
      try { turnstileRef.current?.reset(); } catch { /* ignore */ }
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="scw-anim-fade" style={{ textAlign: "center", padding: "40px 0" }} data-testid="appointment-thank-you">
        <p className="scw-eyebrow" style={{ marginBottom: 12 }}>Confirmed</p>
        <h2 style={{ fontSize: 28, lineHeight: 1.3, margin: 0 }}>{done}</h2>
        <button className="scw-btn scw-btn-ghost" style={{ marginTop: 32 }} onClick={onLeaveSite || onBack} data-testid="appointment-back-btn">Back to swisswatch.com.my</button>
      </div>
    );
  }

  // Steps
  return (
    <div className="scw-anim-fade" onFocus={markStarted} data-testid="appointment-form">
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Book a visit</p>
      <h2 style={{ fontSize: 28, margin: "0 0 8px 0" }}>Reserve time at our showroom.</h2>
      <p style={{ color: "var(--scw-muted)", fontSize: 13, marginBottom: 28 }}>Step {step} of 4</p>

      {step === 1 && (
        <>
          <FieldRow>
            <Label htmlFor="ap-name">Name</Label>
            <input id="ap-name" className="scw-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="appointment-name-input" />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="ap-phone">Phone</Label>
            <input id="ap-phone" className="scw-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Include country code" data-testid="appointment-phone-input" />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="ap-email">Email</Label>
            <input id="ap-email" className="scw-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="appointment-email-input" />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="ap-product">Watch or product of interest</Label>
            <input id="ap-product" className="scw-input" required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g. Rolex Submariner 124060" data-testid="appointment-product-input" />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="ap-purpose">Purpose</Label>
            <select id="ap-purpose" className="scw-select" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="appointment-purpose-select">
              {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {PURPOSE_HINTS[form.purpose] && (
              <p style={{ marginTop: 8, fontSize: 12, lineHeight: 1.6, color: "var(--scw-muted)", fontStyle: "italic" }} data-testid="appointment-purpose-hint">
                {PURPOSE_HINTS[form.purpose]}
              </p>
            )}
          </FieldRow>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <SpinnerButton variant="ghost" type="button" onClick={onBack} dataTestId="appointment-back-step1-btn">Back</SpinnerButton>
            <SpinnerButton
              type="button"
              dataTestId="appointment-next-step1-btn"
              onClick={() => {
                if (!form.name || !form.phone || !form.email || !form.product) { setError("Please fill in all required fields."); return; }
                setError(""); setStep(2);
              }}
            >Continue</SpinnerButton>
          </div>
          {error && <p style={{ color: "#a13b3b", fontSize: 13, marginTop: 12 }} data-testid="appointment-error-step1">{error}</p>}
        </>
      )}

      {step === 2 && (
        <>
          <p style={{ marginTop: -16, marginBottom: 22, fontSize: 13, lineHeight: 1.7, color: "var(--scw-muted)" }} data-testid="appointment-step2-helper">
            Greyed dates are {store === "JB" ? "Mondays" : "Sundays"} or public holidays. For private viewings on those days, please{" "}
            <button
              type="button"
              onClick={onChooseWhatsApp}
              style={{
                background: "none", border: "none", padding: 0, margin: 0,
                font: "inherit", color: "#5C5C5C", textDecoration: "underline",
                cursor: "pointer",
              }}
              data-testid="appointment-step2-whatsapp-link"
            >
              chat on WhatsApp
            </button>{" "}
            and we{"\u2019"}ll arrange it.
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }}
                    onClick={() => {
                      const m = viewMonth.month - 1;
                      if (m < 0) setViewMonth({ year: viewMonth.year - 1, month: 11 });
                      else setViewMonth({ year: viewMonth.year, month: m });
                    }} data-testid="cal-prev-btn">{'\u2190'} Prev</button>
            <h3 style={{ margin: 0 }} data-testid="cal-title">{new Date(viewMonth.year, viewMonth.month, 1).toLocaleString("en-US", { month: "long", year: "numeric" })}</h3>
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }}
                    onClick={() => {
                      const m = viewMonth.month + 1;
                      if (m > 11) setViewMonth({ year: viewMonth.year + 1, month: 0 });
                      else setViewMonth({ year: viewMonth.year, month: m });
                    }} data-testid="cal-next-btn">Next {'\u2192'}</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
            {[0,1,2,3,4,5,6].map((i) => (
              <div key={i} style={{ textAlign: "center", fontSize: 11, color: "var(--scw-muted)", letterSpacing: "0.16em", padding: "8px 0" }}>{dayLabel(i)}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
            {calendarGrid(viewMonth.year, viewMonth.month).map((d, idx) => {
              if (d === null) return <div key={idx} />;
              const dateStr = `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const info = dateMap[dateStr];
              const avail = info?.available;
              const selected = form.appointment_date === dateStr;
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={!avail}
                  onClick={() => setForm({ ...form, appointment_date: dateStr, appointment_time: "" })}
                  className={`scw-slot ${selected ? "is-active" : ""}`}
                  style={{ aspectRatio: "1 / 1", fontSize: 14 }}
                  data-testid={`cal-day-${dateStr}`}
                  title={info?.reason || ""}
                >
                  {d}
                </button>
              );
            })}
          </div>
          {/* Legend — clarifies which cells are bookable. Added Pass 14 after
              showroom feedback that customers were tapping greyed cells
              expecting the form to advance. */}
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 16, fontSize: 12, color: "var(--scw-muted)", letterSpacing: "0.06em" }}
            data-testid="cal-legend"
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-block", width: 14, height: 14, background: "#FFFFFF", border: "1px solid #5C5C5C" }} />
              Available
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-block", width: 14, height: 14, background: "#5C5C5C", border: "1px solid #5C5C5C" }} />
              Selected
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-block", width: 14, height: 14, background: "#f4f4f4", border: "1px solid #eaeaea" }} />
              Closed ({store === "JB" ? "Monday" : "Sunday"} / public holiday)
            </span>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <SpinnerButton variant="ghost" type="button" onClick={() => setStep(1)} dataTestId="appointment-back-step2-btn">Back</SpinnerButton>
            <SpinnerButton type="button" disabled={!form.appointment_date}
              onClick={() => { setError(""); setStep(3); }} dataTestId="appointment-next-step2-btn">Continue</SpinnerButton>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <p className="scw-eyebrow" style={{ marginBottom: 16 }}>Choose a time on {form.appointment_date}</p>
          {slots.length === 0 ? (
            <p style={{ color: "var(--scw-muted)" }} data-testid="appointment-no-slots">No slots available. Please pick another date.</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 10 }}>
              {slots.map((t) => (
                <button
                  key={t} type="button"
                  className={`scw-slot ${form.appointment_time === t ? "is-active" : ""}`}
                  onClick={() => setForm({ ...form, appointment_time: t })}
                  data-testid={`appointment-slot-${t}`}
                >{t}</button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <SpinnerButton variant="ghost" type="button" onClick={() => setStep(2)} dataTestId="appointment-back-step3-btn">Back</SpinnerButton>
            <SpinnerButton type="button" disabled={!form.appointment_time}
              onClick={() => setStep(4)} dataTestId="appointment-next-step3-btn">Review</SpinnerButton>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <p className="scw-eyebrow" style={{ marginBottom: 16 }}>Review &amp; confirm</p>
          <div style={{ borderTop: "1px solid var(--scw-line)", marginBottom: 20 }}>
            {[
              ["Name", form.name], ["Phone", form.phone], ["Email", form.email],
              ["Watch / Product", form.product || "\u2014"],
              ["Purpose", form.purpose], ["Date", form.appointment_date], ["Time", form.appointment_time + " MYT"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", padding: "12px 0", borderBottom: "1px solid var(--scw-line)" }}>
                <div style={{ width: 140, color: "var(--scw-muted)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>{k}</div>
                <div style={{ flex: 1 }} data-testid={`appointment-review-${k.toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>
          <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{ display: "none" }} aria-hidden="true" />
          <TurnstileWidget siteKey={cfg.turnstile_site_key} ref={turnstileRef} />
          {error && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }} data-testid="appointment-error-step4">{error}</p>}
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <SpinnerButton variant="ghost" type="button" onClick={() => setStep(3)} dataTestId="appointment-back-step4-btn">Back</SpinnerButton>
            <SpinnerButton loading={loading} type="button" onClick={submit} dataTestId="appointment-confirm-btn">Confirm booking</SpinnerButton>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Main page ----------

export default function PublicHome() {
  usePageMeta({
    title: "A direct line to Our Atelier \u2014 Swiss Connection Watches",
    robots: "index, follow",
    jsonLd: PUBLIC_HOME_JSONLD,
  });
  const utm = useUtm();
  const location = useLocation();
  const [cfg, setCfg] = useState({ turnstile_site_key: "" });
  const [mode, setMode] = useState("home"); // home | email | whatsapp | appointment
  // Pass 43 — store selection. KL default (zero friction for existing
  // traffic). Persisted to sessionStorage so a customer who flips to
  // JB, completes a flow, and returns to home doesn't get bounced
  // back to KL.
  const [store, setStore] = useState(() => {
    try {
      const v = sessionStorage.getItem("scw_store");
      return v === "JB" ? "JB" : "KL";
    } catch { return "KL"; }
  });
  const handleStoreChange = (next) => {
    setStore(next);
    try { sessionStorage.setItem("scw_store", next); } catch { /* ignore */ }
  };

  useEffect(() => {
    publicApi.config().then((r) => setCfg(r.data)).catch(() => {});
    // Cache where the customer came from so a completed flow can return
    // them to the marketing site (a product page, etc.) — not back to
    // the contact hub home.
    rememberReturnUrl();
  }, []);

  // Reset the form state whenever the user re-navigates to "/" (e.g. by
  // clicking the "Contact Hub" header link from inside a sub-flow). The
  // URL hasn't changed (already "/"), but React Router gives us a fresh
  // location.key per navigation event, so we can use that as a signal.
  useEffect(() => {
    if (location.pathname === "/") setMode("home");
  }, [location.key, location.pathname]);

  const leaveSite = () => {
    const url = getReturnUrl();
    window.location.href = url;
  };

  const storeProfile = store === "JB"
    ? {
        name: cfg.store_name_jb || "Swiss Connection Watches \u2014 JB",
        address: cfg.store_address_jb || "",
        phone: cfg.store_phone_jb || "",
        hours: "Tue\u2013Sun, 11:30 \u2013 18:00 (Closed Mondays)",
      }
    : {
        name: cfg.store_name_kl || "Swiss Connection Watches \u2014 KL",
        address: cfg.store_address_kl || "",
        phone: cfg.store_phone_kl || "",
        hours: "Mon\u2013Fri 11:30 \u2013 17:30, Sat 11:30 \u2013 14:30 (Closed Sundays)",
      };

  return (
    <PublicLayout>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 28px 80px 28px" }}>
        {/* Store segmented toggle — visible on every screen of the hub
            so the customer can switch stores at any step. Defaults to
            KL on first visit (back-compat with all existing traffic). */}
        <div
          className="scw-store-toggle"
          role="tablist"
          aria-label="Choose store"
          data-testid="store-toggle"
          style={{ display: "flex", marginBottom: 18, maxWidth: 520 }}
        >
          {[
            { code: "KL", label: "Kuala Lumpur" },
            { code: "JB", label: "Johor Bahru" },
          ].map((opt) => {
            const active = store === opt.code;
            return (
              <button
                key={opt.code}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handleStoreChange(opt.code)}
                className={`scw-store-toggle-btn${active ? " is-active" : ""}`}
                data-testid={`store-toggle-${opt.code.toLowerCase()}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Store profile card under the toggle — address + hours. Hidden
            on the sub-flow screens to keep them focused. */}
        {mode === "home" && (
          <div
            className="scw-store-profile"
            data-testid="store-profile"
            style={{ marginBottom: 40, maxWidth: 520, fontSize: 13, color: "#5C5C5C", lineHeight: 1.6 }}
          >
            <div data-testid="store-profile-name" style={{ fontWeight: 500, color: "#3D3D3D" }}>
              {storeProfile.name}
            </div>
            {storeProfile.address && (
              <div data-testid="store-profile-address">{storeProfile.address}</div>
            )}
            <div data-testid="store-profile-hours" style={{ marginTop: 4, fontStyle: "italic" }}>
              {storeProfile.hours}
            </div>
          </div>
        )}

        {mode === "home" && (
          <>
            <div style={{ maxWidth: 720, marginBottom: 56 }}>
              <p className="scw-eyebrow" style={{ marginBottom: 14 }}>Contact</p>
              <h1 style={{ fontSize: 31, lineHeight: 1.15, margin: 0, fontWeight: 500 }}>
                A direct line to Our Atelier.
              </h1>
              <p style={{ marginTop: 18, fontSize: 16, lineHeight: 1.7, color: "#5C5C5C", maxWidth: 560 }}>
                Choose the way that suits you. Each enquiry is routed to the specialist on duty and answered personally.
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
              <button type="button" className="scw-card scw-card-link" style={{ textAlign: "left" }} onClick={() => setMode("email")} data-testid="option-email-card">
                <p className="scw-eyebrow" style={{ marginBottom: 14 }}>Option I</p>
                <h3 style={{ fontSize: 22, margin: "0 0 10px 0" }}>Send a message</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "#5C5C5C", margin: 0 }}>Looking for something specific? Tell us.</p>
                <p style={{ marginTop: 24, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}>Begin {'\u2192'}</p>
              </button>
              <button type="button" className="scw-card scw-card-link" style={{ textAlign: "left" }} onClick={() => setMode("whatsapp")} data-testid="option-whatsapp-card">
                <p className="scw-eyebrow" style={{ marginBottom: 14 }}>Option II</p>
                <h3 style={{ fontSize: 22, margin: "0 0 10px 0" }}>Chat on WhatsApp</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "#5C5C5C", margin: 0 }}>Prefer WhatsApp? Two fields and we'll continue there.</p>
                <p style={{ marginTop: 24, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}>Open chat {'\u2192'}</p>
              </button>
              <button type="button" className="scw-card scw-card-link" style={{ textAlign: "left" }} onClick={() => setMode("appointment")} data-testid="option-appointment-card">
                <p className="scw-eyebrow" style={{ marginBottom: 14 }}>Option III</p>
                <h3 style={{ fontSize: 22, margin: "0 0 10px 0" }}>Book a visit</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "#5C5C5C", margin: 0 }}>Visit us in person. Thirty minutes, one specialist, your time.</p>
                <p style={{ marginTop: 24, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}>Choose date {'\u2192'}</p>
              </button>
            </div>
          </>
        )}

        {mode !== "home" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", maxWidth: 620, margin: "0 auto" }}>
            {mode === "email" && <EmailForm cfg={cfg} utm={utm} store={store} onBack={() => setMode("home")} onLeaveSite={leaveSite} />}
            {mode === "whatsapp" && <WhatsAppForm cfg={cfg} utm={utm} store={store} onBack={() => setMode("home")} />}
            {mode === "appointment" && <AppointmentForm cfg={cfg} utm={utm} store={store} onBack={() => setMode("home")} onLeaveSite={leaveSite} onChooseWhatsApp={() => setMode("whatsapp")} />}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
